import React, { useState, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { Check, Copy } from "lucide-react";

const theme = {
  'pre[class*="language-"]': { background: "rgba(0,0,0,0.4)", margin: 0, padding: "1rem", borderRadius: "8px", fontSize: "12px", lineHeight: 1.6 },
  'code[class*="language-"]': { color: "#e2e8f0", fontFamily: "'JetBrains Mono', monospace", fontSize: "12px" },
  comment: { color: "#64748b" }, keyword: { color: "#c084fc" }, string: { color: "#34d399" },
  function: { color: "#60a5fa" }, number: { color: "#fbbf24" }, operator: { color: "#94a3b8" },
  punctuation: { color: "#94a3b8" }, "class-name": { color: "#22d3ee" }, builtin: { color: "#22d3ee" },
  boolean: { color: "#fbbf24" }, property: { color: "#60a5fa" }, tag: { color: "#f87171" },
  "attr-name": { color: "#fbbf24" }, "attr-value": { color: "#34d399" },
};

function CodeBlock({ language, value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="relative group my-2 rounded-lg overflow-hidden border border-cx-border">
      <div className="flex items-center justify-between px-3 py-1.5 bg-black/30 border-b border-cx-border">
        <span className="text-[10px] cx-mono text-cx-muted uppercase">{language || "text"}</span>
        <button onClick={copy} className="cx-btn cx-btn-ghost !px-1.5 !py-0.5 !text-[10px] opacity-0 group-hover:opacity-100 transition-opacity" data-testid="code-copy-btn">
          {copied ? <><Check size={10} /> Copied</> : <><Copy size={10} /> Copy</>}
        </button>
      </div>
      <SyntaxHighlighter language={language || "text"} style={theme} customStyle={{ background: "rgba(0,0,0,0.3)", margin: 0, padding: "12px 14px", fontSize: "12px" }}
        codeTagProps={{ style: { fontFamily: "'JetBrains Mono', monospace" } }}>{value}</SyntaxHighlighter>
    </div>
  );
}

/* Stable component overrides — defined outside render to avoid lint react/no-unstable-nested-components */
function MdCode({ node, inline, className, children, ...props }) {
  const match = /language-(\w+)/.exec(className || "");
  if (!inline && (match || String(children).includes("\n"))) {
    return <CodeBlock language={match?.[1]} value={String(children).replace(/\n$/, "")} />;
  }
  return <code className={className} {...props}>{children}</code>;
}

function MdTable({ children }) {
  return <div className="overflow-auto my-2 rounded border border-cx-border"><table className="cx-table !text-xs">{children}</table></div>;
}

const MD_COMPONENTS = { code: MdCode, table: MdTable };

export default function MarkdownMessage({ content }) {
  if (!content) return null;
  return (
    <div className="prose prose-invert prose-sm max-w-none text-sm leading-relaxed
      prose-p:my-1.5 prose-headings:mt-3 prose-headings:mb-1.5 prose-headings:font-semibold
      prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5
      prose-a:text-cx-emerald prose-a:no-underline hover:prose-a:underline
      prose-strong:text-cx-text prose-code:text-cx-cyan prose-code:bg-black/30 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:font-mono
      prose-table:border-cx-border prose-th:border-cx-border prose-td:border-cx-border
      prose-blockquote:border-cx-emerald/30 prose-blockquote:bg-black/10 prose-blockquote:rounded prose-blockquote:py-1 prose-blockquote:px-3"
      data-testid="markdown-content">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{content}</ReactMarkdown>
    </div>
  );
}
